import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@material-ui/core';

import { DocumentsActions } from 'state/modules/documents';
import { useStyles } from './UploadDocumentModal.styled';
import { TextField } from 'components/FormFields';
import { BaseModal } from 'components/UI';
import ImageTools from 'lib/imageTools';
import { UserContext } from 'context';
import moment from 'moment';

const UploadVehicleImageSchema = Yup.object().shape({
  name: Yup.string().required('This field is required'),
});

const UploadDocumentModal = ({ data, open, handleClick }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [file, setFile] = useState();
  const { orgType } = useContext(UserContext);
  const [thumbnail, setThumbnail] = useState();

  const { fetchedUploadDocument, fetchingUploadDocument } = useSelector((state) => state.documents);

  const generateThumbnail = (file) => {
    if (file) {
      const ImageTool = new ImageTools();
      const reader = new FileReader();
      reader.onloadend = () => {
        ImageTool.resize(file, { width: '100%', height: 300 }).then((_file) => {
          const image = URL.createObjectURL(_file);
          setThumbnail(image);
        });
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Handle Image Change
   * @param {object} event Files input event
   */
  const handleUploadFile = (event) => {
    event.preventDefault();
    // get file from event object
    const file = event.target.files[0];
    // verify if an image is selected
    if (file) {
      setFile(file);
      generateThumbnail(file);
    }
  };

  useEffect(() => {
    if (fetchedUploadDocument) {
      handleClick(null, '', true);
      dispatch(DocumentsActions.resetUploadDocument());
    }
  }, [fetchedUploadDocument, handleClick, dispatch]);

  const onSubmit = () => {
    let formData = new FormData();

    formData.append('data', file);
    formData.append('name', data.key);
    formData.append('tags', data.key);
    formData.append('overwrite', true);
    formData.append('version', Date.now());
    formData.append('source_id', data.sourceId);
    formData.append('source_type', data.sourceType);
    formData.append('is_bastion_shared', orgType === 'msServiceOrg');

    dispatch(DocumentsActions.fetchUploadDocument(formData));
  };

  return (
    <BaseModal
      open={open}
      hasFooter={false}
      hasCancelButton={false}
      enableCloseButton={false}
      size={file ? 'md' : 'sm'}
      title={i18n.t('modals.upload_document')}
    >
      <Formik
        enableReinitialize
        initialValues={{
          name: file?.name || moment().format('x'),
        }}
        validationSchema={UploadVehicleImageSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={3} textAlign="center">
              {thumbnail && (
                <iframe
                  className={classes.iframe}
                  src={thumbnail}
                  frameBorder="0"
                  title="doc-file"
                />
              )}
            </Box>
            {file && (
              <Box mt={1}>
                <Field
                  name="name"
                  component={TextField}
                  label={i18n.t('modals.document')}
                  inputProps={{ readOnly: true }}
                />
              </Box>
            )}
            <Box mt={5} display="flex" justifyContent="flex-end">
              <Button
                type="button"
                variant="text"
                onClick={handleClick}
                className={classes.button}
                disabled={fetchingUploadDocument}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Box className={classes.root}>
                <input
                  multiple
                  type="file"
                  className={classes.input}
                  id="contained-button-file"
                  onChange={handleUploadFile}
                />
                <label htmlFor="contained-button-file">
                  <Button
                    type="button"
                    color="primary"
                    component="span"
                    className={classes.button}
                    style={{ textTransform: 'uppercase' }}
                  >
                    {i18n.t('modals.select_document')}
                  </Button>
                </label>
              </Box>
              {file && (
                <Button
                  type="submit"
                  variant="text"
                  color="primary"
                  className={classes.button}
                  disabled={fetchingUploadDocument}
                >
                  {i18n.t('modals.upload_document')}
                </Button>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

UploadDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

UploadDocumentModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default UploadDocumentModal;
